import i18n from '../i18n';
import { apiErrorCode } from '../api/client';

/**
 * The multer limits on the server, by what they guard:
 * `utils/documentUploads.ts`, `services/documentAttachments.service.ts` and
 * `modules/photos/photos.controller.ts`.
 *
 * The server is still what enforces them — it answers 413 `FILE_TOO_LARGE` —
 * but a file rejected here is never uploaded, which is the difference between
 * being told now and being told after a minute of upload.
 */
export const UPLOAD_MAX_MB = {
  /** Notes attachments and the documents the AI import reads. */
  document: 50,
  /** Tickets, confirmations, receipts and the profile photo. */
  media: 10,
  /** Gallery photos and the story background. */
  photo: 25,
} as const;

export type UploadKind = keyof typeof UPLOAD_MAX_MB;

/** Why the server would refuse this file, or null when it fits. */
export function tooLargeMessage(file: File, kind: UploadKind): string | null {
  const mb = UPLOAD_MAX_MB[kind];
  if (file.size <= mb * 1024 * 1024) return null;
  return i18n.t('upload.fileTooLarge', { name: file.name, size: `${mb} MB` });
}

/** Keeps the files that fit and describes each one it dropped. */
export function withinLimit(files: File[], kind: UploadKind): { accepted: File[]; errors: string[] } {
  const accepted: File[] = [];
  const errors: string[] = [];
  for (const file of files) {
    const tooLarge = tooLargeMessage(file, kind);
    if (tooLarge) errors.push(tooLarge);
    else accepted.push(file);
  }
  return { accepted, errors };
}

/**
 * What to say when the server refused an upload. Falls back to the caller's own
 * message for anything that isn't about the file itself.
 */
export function uploadErrorMessage(error: unknown, name: string, kind: UploadKind, fallback: string): string {
  const code = apiErrorCode(error);
  if (code === 'FILE_TOO_LARGE') {
    return i18n.t('upload.fileTooLarge', { name, size: `${UPLOAD_MAX_MB[kind]} MB` });
  }
  if (code === 'UNSUPPORTED_FILE_TYPE') return i18n.t('upload.fileUnsupported', { name });
  if (code === 'IMAGE_DECODE_FAILED') return i18n.t('upload.imageUnreadable', { name });
  if (code === 'STORAGE_QUOTA_EXCEEDED') return i18n.t('upload.storageFull');
  return fallback;
}
